import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { fetchEpg } from "../Broadcast/channelScheduleApi";
import AdPlacement from "../Advertising/AdPlacement";
import {
  DISPLAY_AD_SIZE_ATTRIBUTES,
  readDisplayAdSize,
} from "../Advertising/displayAdSize";
import AppearanceButton from "../Appearance/AppearanceButton";
import DestinationExperienceGate from "../Experience/DestinationExperienceGate";
import { navigationPagePresentation } from "../Navigation/navigationApi";
import {
  computeGuideTimeline,
  isOpenEndedProgram,
  programSegmentGeometry,
  isSameGuideProgram,
} from "./guideTimeline";
import { resolveChannelGuidePrograms } from "./channelProgramPanel";
import "./GuidePage.css";

function formatClock(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
}

function programKey(p, chId) {
  return String(
    (chId || "") +
      ":" +
      (p.id || p.unique_id || (p.title || "") + ":" + (p.starts_at || ""))
  );
}

function nextProgramStartMs(programs, index) {
  for (var i = index + 1; i < programs.length; i++) {
    var n = programs[i];
    if (n && n.starts_at) {
      var t = new Date(n.starts_at).getTime();
      if (isFinite(t)) return t;
    }
  }
  return null;
}

const GUIDE_AD_MAX_SCALE = 1;

/**
 * Fits the guide_top_banner display creative (served size, else the selected
 * unit) to the ad row: native size centered when it fits, scaled down with its
 * aspect ratio when narrower, never upscaled. Writes --guide-ad-* on the row
 * (mapped in GuidePage.css); independent of the Home page banner.
 */
function useGuideAdFit(host) {
  useEffect(() => {
    if (
      !host ||
      typeof window === "undefined" ||
      typeof window.ResizeObserver !== "function"
    ) {
      return undefined;
    }
    const setVar = (prop, value) => {
      if (host.style.getPropertyValue(prop) !== value) host.style.setProperty(prop, value);
    };
    const sync = () => {
      const unit = host.querySelector(".display-ad");
      const size = readDisplayAdSize(unit);
      const width = unit ? unit.getBoundingClientRect().width : 0;
      if (!size || !(width > 0)) return;
      // Rounded down so the scaled creative never exceeds the fractional box.
      const scale = Math.min(GUIDE_AD_MAX_SCALE, Math.floor((width / size.width) * 10000) / 10000);
      setVar("--guide-ad-fit-width", `${Math.floor(size.width * scale * 100) / 100}px`);
      setVar("--guide-ad-fit-height", `${Math.floor(size.height * scale * 100) / 100}px`);
      setVar("--guide-ad-width", `${size.width}px`);
      setVar("--guide-ad-height", `${size.height}px`);
      setVar("--guide-ad-ratio", `${size.width} / ${size.height}`);
      setVar("--guide-ad-scale", scale.toFixed(4));
    };
    const resizeObserver = new window.ResizeObserver(sync);
    resizeObserver.observe(host);
    const unitObserver =
      typeof window.MutationObserver === "function" ? new window.MutationObserver(sync) : null;
    if (unitObserver) {
      unitObserver.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: DISPLAY_AD_SIZE_ATTRIBUTES,
      });
    }
    sync();
    return () => {
      resizeObserver.disconnect();
      if (unitObserver) unitObserver.disconnect();
    };
  }, [host]);
}

/**
 * Public multi-channel TV Guide — all active/published TPG Scheduler channels
 * via /channels/epg (no Home subcategory assignment required).
 *
 * Page title / subtitle / description / content come from Navigation Item
 * (system_key=guide) via Appearance Engine navigation-page presentation —
 * not a second Guide CMS.
 */
function GuidePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [adTop, setAdTop] = useState(null);
  useGuideAdFit(adTop);

  const presentation = useMemo(
    () => navigationPagePresentation("guide") || {},
    []
  );
  const pageTitle = presentation.title || "Guide";
  const pageSubtitle = presentation.subtitle || "";
  const pageDescription = presentation.description || "";
  const pageContent = presentation.content || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const from = new Date();
        from.setMinutes(0, 0, 0);
        const to = new Date(from.getTime() + 6 * 3600000);
        const grid = await fetchEpg({
          from: from.toISOString(),
          to: to.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        });
        if (!cancelled) {
          setData(grid);
          setError(grid ? "" : "No EPG data.");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Guide failed to load.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const windowMeta = useMemo(() => {
    if (!data) return null;
    const meta = computeGuideTimeline(data.from, data.to);
    const pxPerMs = 200 / meta.hourMs;
    const widthPx = Math.max(900, Math.round(meta.span * pxPerMs));
    return {
      start: meta.start,
      end: meta.end,
      span: meta.span,
      hours: meta.hours,
      widthPx: widthPx,
      nowLeft: meta.nowLeft,
    };
  }, [data]);

  return (
    <DestinationExperienceGate destinationType="guide">
    <div className="tpg-guide">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <header className="tpg-guide__header">
        <div>
          {pageSubtitle ? (
            <p className="tpg-guide__eyebrow">{pageSubtitle}</p>
          ) : null}
          <h1>{pageTitle}</h1>
          {pageDescription ? (
            <p className="tpg-guide__sub">{pageDescription}</p>
          ) : null}
        </div>
      </header>

      <div className="tpg-guide__ad-top" ref={setAdTop}>
        <AdPlacement
          placementKey="guide_top_banner"
          variant="banner"
          limit={1}
          className="tpg-guide__ad-placement"
        />
      </div>

      {loading && <p className="tpg-guide__status">Loading guide…</p>}
      {error && <p className="tpg-guide__error">{error}</p>}

      {data && windowMeta ? (
        <div className="tpg-guide__layout">
          {/*
            Channel rail is outside the horizontal scroller so logo/#/name
            never leave the Guide viewport. Timeline alone scrolls X.
          */}
          <div className="tpg-guide__scroll">
            <div className="tpg-guide__stage">
            <div className="tpg-guide__epg">
              <div className="tpg-guide__channel-rail" aria-label="Channels">
                <div className="tpg-guide__hours-gutter">CHANNELS</div>
                {(data.channels || []).map((row) => {
                  const ch = row.channel || {};
                  const slug = ch.slug || ch.unique_id;
                  const channelPath = "/channels/" + (slug || ch.id || "");
                  const channelLabel =
                    (ch.channel_number != null
                      ? String(ch.channel_number) + " "
                      : "") + (ch.name || "Channel");
                  return (
                    <div
                      className="tpg-guide__channel"
                      key={"ch-" + (ch.id || slug)}
                    >
                      <Link
                        to={channelPath}
                        className="tpg-guide__channel-link"
                        aria-label={channelLabel}
                        title={channelLabel}
                      >
                        {ch.channel_number != null ? (
                          <em className="tpg-guide__channel-num">
                            {ch.channel_number}
                          </em>
                        ) : (
                          <em
                            className="tpg-guide__channel-num tpg-guide__channel-num--empty"
                            aria-hidden="true"
                          >
                            —
                          </em>
                        )}
                        {ch.logo ? (
                          <span className="tpg-guide__logo-box">
                            <img
                              src={ch.logo}
                              alt=""
                              className="tpg-guide__logo"
                            />
                          </span>
                        ) : (
                          <span
                            className="tpg-guide__logo-box tpg-guide__logo-box--ph"
                            aria-hidden="true"
                          />
                        )}
                        <span className="tpg-guide__channel-meta">
                          <strong title={ch.name || ""}>{ch.name}</strong>
                        </span>
                      </Link>
                    </div>
                  );
                })}
              </div>

              <div className="tpg-guide__timeline-viewport">
                <div
                  className={
                    "tpg-guide__timeline" +
                    (selected ? " has-selection" : "")
                  }
                  style={{ width: windowMeta.widthPx + "px" }}
                >
                  <div className="tpg-guide__hours-track">
                    {windowMeta.hours.map((t) => (
                      <div
                        key={t}
                        className="tpg-guide__hour"
                        style={{
                          left:
                            ((t - windowMeta.start) / windowMeta.span) * 100 +
                            "%",
                        }}
                      >
                        {new Date(t).toLocaleTimeString([], {
                          hour: "numeric",
                        })}
                      </div>
                    ))}
                  </div>

                  {windowMeta.nowLeft >= 0 && windowMeta.nowLeft <= 100 ? (
                    <div
                      className="tpg-guide__now-plane"
                      style={{ left: windowMeta.nowLeft + "%" }}
                      aria-hidden="true"
                    />
                  ) : null}

                  {(data.channels || []).map((row) => {
                    const ch = row.channel || {};
                    const slug = ch.slug || ch.unique_id;
                    const channelPath = "/channels/" + (slug || ch.id || "");
                    const current = (row.now && row.now.current) || null;
                    const programs = resolveChannelGuidePrograms(
                      Array.isArray(row.programs) ? row.programs : [],
                      {
                        success: true,
                        current: current,
                        next: (row.now && row.now.next) || null,
                      }
                    );
                    return (
                      <div
                        className="tpg-guide__programs"
                        key={"tl-" + (ch.id || slug)}
                      >
                        {programs.map((p, pIndex) => {
                          const geom = programSegmentGeometry(
                            p,
                            windowMeta.start,
                            windowMeta.end,
                            nextProgramStartMs(programs, pIndex)
                          );
                          if (!geom) {
                            return null;
                          }
                          const isNow = isSameGuideProgram(current, p);
                          const isSelected =
                            selected && selected.key === programKey(p, ch.id);
                          const openEnded = isOpenEndedProgram(p);
                          return (
                            <button
                              type="button"
                              key={programKey(p, ch.id)}
                              className={
                                "tpg-guide__seg" +
                                (isNow ? " is-now" : "") +
                                (isSelected ? " is-selected" : "") +
                                (openEnded ? " is-open-ended" : "")
                              }
                              style={{
                                left: geom.left + "%",
                                width: geom.width + "%",
                              }}
                              aria-pressed={isSelected ? "true" : "false"}
                              aria-current={isNow ? "true" : undefined}
                              aria-label={
                                (p.title || p.entry_kind || "Program") +
                                (isNow ? " (live now)" : "") +
                                (openEnded ? " (24/7)" : "")
                              }
                              onClick={() =>
                                setSelected({
                                  key: programKey(p, ch.id),
                                  channel: ch,
                                  program: p,
                                  channelPath: channelPath,
                                  isNow: !!isNow,
                                })
                              }
                              title={p.title || "Program"}
                            >
                              <strong>
                                {p.title || p.entry_kind || "Program"}
                              </strong>
                              {openEnded ? null : (
                                <span>
                                  {formatClock(p.starts_at)}
                                  {p.ends_at
                                    ? " – " + formatClock(p.ends_at)
                                    : ""}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            </div>
            {!(data.channels || []).length ? (
              <p className="tpg-guide__empty">No live channels available.</p>
            ) : null}
          </div>

          <aside className="tpg-guide__aside">
            {selected ? (
              <>
                <p className="tpg-guide__label">
                  {selected.isNow ? "Now Playing" : "Program"}
                </p>
                <h2>{selected.program.title || "Program"}</h2>
                <p className="tpg-guide__aside-meta">
                  {selected.channel.name}
                  <br />
                  {isOpenEndedProgram(selected.program)
                    ? selected.isNow
                      ? "LIVE · 24/7"
                      : "24/7"
                    : formatClock(selected.program.starts_at) +
                      (selected.program.ends_at
                        ? " – " + formatClock(selected.program.ends_at)
                        : "")}
                </p>
                {selected.program.description ? (
                  <p className="tpg-guide__aside-desc">
                    {selected.program.description}
                  </p>
                ) : null}
                <AppearanceButton
                  variant="primary"
                  to={selected.channelPath}
                  className="tpg-guide__cta"
                >
                  {selected.isNow ? "Watch Live" : "Go to Channel"}
                </AppearanceButton>
              </>
            ) : pageContent ? (
              <p className="tpg-guide__aside-hint">{pageContent}</p>
            ) : null}
          </aside>
        </div>
      ) : null}
    </div>
    </DestinationExperienceGate>
  );
}

export default GuidePage;
